<template>
    <Modal ref="modal" class="change-nb-products-ordered-modal" @closed="onModalClose">
        <template #header="sp">
            <div class="h3">Modifier la quantité commandée</div>
        </template>
        <template #body="sp">
            <div class="form-group row">
                <label for="staticCustomerName" class="col-4 col-form-label">{{ customerTypeLabel }}</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticCustomerName" :value="customerName">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticDeliveryDate" class="col-4 col-form-label">Date de livraison</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticDeliveryDate" :value="formatDate(order.delivery_date)">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticProductName" class="col-4 col-form-label">Produit</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticProductName" :value="productName">
                </div>
            </div>

            <div class="form-group row">
                <label for="quantity" class="col-4 col-form-label">Quantité commandée</label>
                <div class="col-8">
                    <input :readonly="order.paid_at || orderDone" type="text" class="form-control" id="quantity" v-model="quantity" @click="onClickQuantity">
                </div>
                <div v-if="quantityOffered" class="offset-4 col-8 alert alert-warning">
                    <i class="far fa-exclamation-triangle"></i> {{ quantityOffered }} {{ pluralize('produit offert', quantityOffered, 'produits offerts') }}
                </div>
            </div>

            <template v-if="order.paid_at">
                <div class="alert alert-danger">
                    Vous ne pouvez pas modifier la quantité commandée d'une commande payée d'avance.
                </div>
            </template>
            <template v-if="orderDone">
                <div class="alert alert-danger">
                    Vous ne pouvez pas modifier la quantité commandée d'une commande terminée.
                </div>
            </template>

            <ErrorsZone :errors="errors" />
        </template>
        <template #footer v-if="!order.paid_at && !orderDone">
            <AuthorizationModal
                token-type="ChangeNbProductsOrdered"
                :token-payload="authorizationTokenPayload"
                @authorised="onAuthorizationModalAuthorize"
            >
                <template #button="sp">
                    <AuthorizationModalButton class="btn-warning" @click="onClickAddButton(sp)">
                        Modifier la quantité
                    </AuthorizationModalButton>
                </template>
            </AuthorizationModal>
        </template>
    </Modal>
</template>

<script>
    import Modal from "../Modal";
    import ErrorsZone from "../ErrorsZone";
    import AuthorizationModal from "../AuthorizationModal";
    import AuthorizationModalButton from "../AuthorizationModalButton";

    import {usePostRequest} from "../compositions/axios/postRequest";
    import {pluralize} from "../../helpers"

    export default {
        name: 'ChangeNbProductsOrderedModal',

        components: {
            Modal,
            ErrorsZone,
            AuthorizationModal,
            AuthorizationModalButton,
        },

        setup() {
            const {
                loading: sending,
                errors,
                post
            } = usePostRequest();

            return {
                sending,
                errors,
                post
            };
        },

        props: {
            productSku: {
                type: String,
                required: true,
            },
            productName: {
                type: String,
                required: true,
            },
            order: {
                type: Object,
                required: true,
            },
            initialQuantity: {
                type: Number,
                required: true,
            },
            quantityOffered: {
                type: Number,
                required: false,
                default: 0,
            },
        },

        data() {
            return {
                quantity: this.initialQuantity || 0,
            };
        },

        watch: {
            quantity(newValue) {
                let qty = parseInt(newValue);
                if (isNaN(qty) || qty < 0) {
                    qty = 0;
                }
                this.quantity = qty;
            },

            initialQuantity(newValue) {
                this.quantity = newValue;
            },
        },

        methods: {
            onClickQuantity(e) {
                e.target.select();
            },

            showModal() {
                this.$refs.modal.showModal();
            },

            closeModal() {
                this.$refs.modal.closeModal();
            },

            formatDate(date) {
                return moment(date).format('dddd D MMMM Y')
            },

            onModalClose() {
                this.quantity = this.initialQuantity || 0;
                this.errors = null;
            },

            onClickAddButton(sp) {
                if (this.validateFields()) {
                    sp.showModal();
                }
            },

            validateFields() {
                this.errors = null;

                let errors = {};

                if (isNaN(this.quantity)) {
                    errors['quantity'] = ["La quantité est invalide. (nombre entier positif requis)"];
                } else if (this.quantity < 0) {
                    errors['quantity'] = ["La quantité doit être supérieure ou égale à 0."];
                }

                if (Object.keys(errors).length > 0) {
                    this.errors = { message: "Merci de corriger les erreurs suivantes :", errors: errors };
                    return false;
                }

                return true;
            },

            async onAuthorizationModalAuthorize(token) {
                const response = await this.post('/espace-employes/commandes/preparation/commande/' + this.order.id + '/change-nb-produits-commandes', {
                    sku: this.productSku,
                    quantity: this.quantity,
                    authorization_token: token ? token.token : null,
                });

                if (!this.errors) {
                    this.closeModal();

                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },

            pluralize,
        },

        computed: {
            authorizationTokenPayload() {
                return {
                    order_id: this.order.id,
                    product_id: this.productSku,
                    quantity: this.quantity,
                };
            },

            orderDone() {
                return this.order.status === 'INVOICED' || this.order.status === 'DONE';
            },

            customerName() {
                if (this.order.subcustomer) {
                    return this.order.subcustomer.name;
                }
                return this.order.customer.name;
            },

            customerTypeLabel() {
                switch (this.order.customer.type) {
                    case 'store':
                        return 'Magasin';
                    case 'short_channel':
                        return 'Circuit court';
                    case 'customer_group':
                        return 'Groupement de clients';
                    case 'pickup_point':
                        return 'Lieu de retrait';
                    default:
                        return 'Client';
                }
            },
        },
    }
</script>

<style lang="scss">
    .change-nb-products-ordered-modal {
        :first-of-type(.modal-container) {
            max-width: 600px;
            width: auto;
        }
    }
</style>
